import { apiClient } from './api';
import type {
  AccuracyErrorItem,
  AccuracyReportFilters,
  EvaluationRun,
  GuidelineDraft,
  SavedAccuracyReport,
  TuningSession,
} from '../types/accuracy';

export async function listBaselineReports(): Promise<SavedAccuracyReport[]> {
  return await apiClient.get<SavedAccuracyReport[]>('/accuracy/baseline-reports');
}

export async function getBaselineReport(reportId: string): Promise<SavedAccuracyReport> {
  return await apiClient.get<SavedAccuracyReport>(`/accuracy/baseline-reports/${reportId}`);
}

export async function deleteBaselineReport(reportId: string): Promise<void> {
  await apiClient.delete<void>(`/accuracy/baseline-reports/${reportId}`);
}

export async function createBaselineReport(params: {
  name: string;
  filters: AccuracyReportFilters;
}): Promise<SavedAccuracyReport> {
  return await apiClient.post<SavedAccuracyReport>('/accuracy/baseline-reports', params);
}

export async function getBaselineErrors(reportId: string): Promise<AccuracyErrorItem[]> {
  return await apiClient.get<AccuracyErrorItem[]>(
    `/accuracy/baseline-reports/${reportId}/errors`
  );
}

export async function drilldownBaseline(params: {
  reportId: string;
  body: {
    type: AccuracyErrorItem['type'];
    error: AccuracyErrorItem['error'];
    category_id?: string;
    limit?: number;
    offset?: number;
  };
}): Promise<{ total: number; items: AccuracyErrorItem[] }> {
  return await apiClient.post<{ total: number; items: AccuracyErrorItem[] }>(
    `/accuracy/baseline-reports/${params.reportId}/drilldown`,
    params.body
  );
}

export async function listTuningSessions(): Promise<TuningSession[]> {
  return await apiClient.get<TuningSession[]>('/accuracy/tuning-sessions');
}

export async function getTuningSession(sessionId: string): Promise<TuningSession> {
  return await apiClient.get<TuningSession>(`/accuracy/tuning-sessions/${sessionId}`);
}

export async function createTuningSession(params: {
  name: string;
  baseline_report_id: string;
  content_json?: GuidelineDraft['content_json'];
}): Promise<TuningSession> {
  return await apiClient.post<TuningSession>('/accuracy/tuning-sessions', params);
}

export async function getGuidelineDraft(draftId: string): Promise<GuidelineDraft> {
  return await apiClient.get<GuidelineDraft>(`/accuracy/guideline-drafts/${draftId}`);
}

export async function putGuidelineDraft(params: {
  draftId: string;
  content_json: GuidelineDraft['content_json'];
}): Promise<GuidelineDraft> {
  return await apiClient.put<GuidelineDraft>(`/accuracy/guideline-drafts/${params.draftId}`, {
    content_json: params.content_json,
  });
}

export async function listEvaluationRuns(sessionId: string): Promise<EvaluationRun[]> {
  return await apiClient.get<EvaluationRun[]>(
    `/accuracy/tuning-sessions/${sessionId}/evaluation-runs`
  );
}

export async function createEvaluationRun(sessionId: string): Promise<EvaluationRun> {
  return await apiClient.post<EvaluationRun>(
    `/accuracy/tuning-sessions/${sessionId}/evaluation-runs`
  );
}

export async function getEvaluationRun(runId: string): Promise<EvaluationRun> {
  return await apiClient.get<EvaluationRun>(`/accuracy/evaluation-runs/${runId}`);
}

export async function getEvaluationRunErrors(runId: string): Promise<AccuracyErrorItem[]> {
  return await apiClient.get<AccuracyErrorItem[]>(
    `/accuracy/evaluation-runs/${runId}/errors`
  );
}

export async function getMonitoringJobErrors(jobId: string): Promise<AccuracyErrorItem[]> {
  return await apiClient.get<AccuracyErrorItem[]>(
    `/accuracy/monitoring/jobs/${jobId}/errors`
  );
}

export async function getFrozenLabels(refId: string): Promise<unknown> {
  return await apiClient.get(`/accuracy/frozen-labels/${refId}`);
}

export async function publishTuningSession(params: {
  sessionId: string;
  evaluation_run_id: string;
}): Promise<TuningSession> {
  return await apiClient.post<TuningSession>(`/accuracy/tuning-sessions/${params.sessionId}/publish`, {
    evaluation_run_id: params.evaluation_run_id,
  });
}

